import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Stack,
  VStack,
} from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import { disabledInputStyles } from '@diamond/shared/ui';
import { type Control, type UseFormReturn, Controller } from 'react-hook-form';

import type { CreateVoucherRequest } from '../../types';
import { PurchaseProductSelect } from './purchase-product-select';

interface RewardDiscountProductFieldProps {
  control: Control<CreateVoucherRequest>;
  purchaseProductOptions: SelectOption[];
  errorMessage?: string;
  flex?: number;
}

export const RewardDiscountProductField = ({
  control,
  purchaseProductOptions,
  errorMessage,
  flex = 1,
}: RewardDiscountProductFieldProps) => (
  <FormControl isInvalid={Boolean(errorMessage)} flex={flex}>
    <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
      Diskon dari Produk
    </FormLabel>
    <Controller
      control={control}
      name="rewardDiscountProduct"
      render={({ field }) => (
        <PurchaseProductSelect
          value={field.value && field.value.value ? field.value : null}
          onChange={field.onChange}
          options={purchaseProductOptions}
          placeholder="Pilih produk untuk diskon"
          isDisabled={true} // Always disabled - discount product is auto-filled from rule product (Buy A Get A)
        />
      )}
    />
    <FormErrorMessage>{errorMessage}</FormErrorMessage>
  </FormControl>
);

interface AmountRewardFieldsProps {
  form: UseFormReturn<CreateVoucherRequest>;
  purchaseProductOptions: SelectOption[];
  errorMessage?: string;
  productError?: string;
}

export const AmountRewardFields = ({
  form,
  purchaseProductOptions,
  errorMessage,
  productError,
}: AmountRewardFieldsProps) => {
  const { control, register } = form;

  return (
    <VStack spacing={4} align="stretch">
      <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
        <FormControl isInvalid={Boolean(errorMessage)} flex={3}>
          <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
            Potongan Harga
          </FormLabel>
          <InputGroup>
            <InputLeftAddon
              bg="gray.100"
              borderColor="gray.200"
              borderRightRadius={0}
            >
              Rp
            </InputLeftAddon>
            <Input
              type="number"
              min={0}
              fontSize="sm"
              borderColor="gray.200"
              borderRadius="lg"
              borderLeftRadius={0}
              focusBorderColor="blue.500"
              _focus={{
                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
              }}
              {...register('reward_configuration.discount_amount_value', {
                valueAsNumber: true,
              })}
              isDisabled={true} // Always disabled - DISCOUNT_AMOUNT is currently not available
              sx={disabledInputStyles}
            />
          </InputGroup>
          <FormErrorMessage>{errorMessage}</FormErrorMessage>
        </FormControl>

        <RewardDiscountProductField
          control={control}
          purchaseProductOptions={purchaseProductOptions}
          errorMessage={productError}
          flex={7}
        />
      </Stack>
    </VStack>
  );
};

interface PercentRewardFieldsProps {
  form: UseFormReturn<CreateVoucherRequest>;
  purchaseProductOptions: SelectOption[];
  errorMessage?: string;
  productError?: string;
}

export const PercentRewardFields = ({
  form,
  purchaseProductOptions,
  errorMessage,
  productError,
}: PercentRewardFieldsProps) => {
  const { control, register } = form;

  return (
    <VStack spacing={4} align="stretch">
      <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
        <FormControl isInvalid={Boolean(errorMessage)} flex={3}>
          <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
            Jumlah Diskon
          </FormLabel>
          <InputGroup>
            <Input
              type="number"
              min={0}
              max={100}
              fontSize="sm"
              borderColor="gray.200"
              borderRadius="lg"
              focusBorderColor="blue.500"
              textAlign="center"
              _focus={{
                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
              }}
              {...register('reward_configuration.discount_percentage_value', {
                valueAsNumber: true,
              })}
            />
            <InputRightAddon
              bg="gray.100"
              borderColor="gray.200"
              borderLeftRadius={0}
            >
              %
            </InputRightAddon>
          </InputGroup>
          <FormErrorMessage>{errorMessage}</FormErrorMessage>
        </FormControl>

        <RewardDiscountProductField
          control={control}
          purchaseProductOptions={purchaseProductOptions}
          errorMessage={productError}
          flex={7}
        />
      </Stack>
    </VStack>
  );
};

interface FreeGoodsRewardFieldsProps {
  form: UseFormReturn<CreateVoucherRequest>;
  purchaseProductOptions: SelectOption[];
  productError?: string;
  quantityError?: string;
}

export const FreeGoodsRewardFields = ({
  form,
  purchaseProductOptions,
  productError,
  quantityError,
}: FreeGoodsRewardFieldsProps) => {
  const { control, register } = form;

  return (
    <VStack spacing={4} align="stretch">
      <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
        <FormControl isInvalid={Boolean(productError)} flex={7}>
          <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
            Reward Produk Gratis
          </FormLabel>
          <Controller
            control={control}
            name="rewardFreeProduct"
            render={({ field }) => (
              <PurchaseProductSelect
                value={field.value && field.value.value ? field.value : null}
                onChange={field.onChange}
                options={purchaseProductOptions}
                placeholder="Pilih produk gratis"
                isDisabled={true} // Always disabled - FREE_GOODS product is auto-filled from rule product (Buy A Get A)
              />
            )}
          />
          <FormErrorMessage>{productError}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(quantityError)} flex={3}>
          <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
            Kuantitas produk
          </FormLabel>
          <Input
            type="number"
            min={1}
            fontSize="sm"
            textAlign="center"
            borderRadius="lg"
            borderColor="gray.200"
            focusBorderColor="blue.500"
            _focus={{
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
            }}
            {...register('reward_configuration.free_goods_quantity_value', {
              valueAsNumber: true,
            })}
            isDisabled={true} // Always disabled - FREE_GOODS quantity is fixed at 1
            sx={disabledInputStyles}
          />
          <FormErrorMessage>{quantityError}</FormErrorMessage>
        </FormControl>
      </Stack>
    </VStack>
  );
};
